import React, { useState, useRef, useEffect } from 'react';
import HelpPanel from './HelpPanel.jsx';
import { RunBtn } from '../../components/ui';

const DIRS = [[-1,0],[1,0],[0,-1],[0,1]];
function idx(x,y,w){return x + y*w}

export default function PolicyGradientLab(){
  const W = 5, H = 5;
  const start = [0,0];
  const goal = [4,4];
  const walls = useRef(new Set([idx(1,2,W), idx(2,2,W)]));

  // policy params: logits = Wp * onehot(state)  -> 4 actions
  const N = W*H;
  const paramsRef = useRef(null);
  function initParams(){ paramsRef.current = { W: new Float32Array(4 * N).fill(0) }; for(let i=0;i<paramsRef.current.W.length;i++) paramsRef.current.W[i] = (Math.random()-0.5)*0.1 }
  if (!paramsRef.current) initParams();

  const [agent, setAgent] = useState(start);
  const [lr, setLr] = useState(0.4);
  const [gamma, setGamma] = useState(0.95);
  const [running, setRunning] = useState(false);
  const [episode, setEpisode] = useState(0);
  const [returnsHist, setReturnsHist] = useState([]);
  const [maxSteps, setMaxSteps] = useState(200);

  useEffect(()=> setAgent(start), []);

  function legal(x,y){ if (x<0||y<0||x>=W||y>=H) return false; if (walls.current.has(idx(x,y,W))) return false; return true }
  function stepState(pos,a){ const nx = pos[0]+DIRS[a][0], ny = pos[1]+DIRS[a][1]; if (!legal(nx,ny)) return pos; return [nx,ny]; }
  function isGoal(pos){ return pos[0]===goal[0] && pos[1]===goal[1]; }

  function softmax(logits){ const max = Math.max(...logits); const ex = logits.map(v => Math.exp(v-max)); const s = ex.reduce((a,b)=>a+b,0); return ex.map(v=>v/s) }

  function policyProbs(stateIndex){ const p = paramsRef.current; const logits = new Array(4); for(let a=0;a<4;a++){ logits[a] = p.W[a*N + stateIndex] } return softmax(logits) }

  function sampleAction(probs){ let r = Math.random(); for(let a=0;a<probs.length;a++){ r -= probs[a]; if (r<=0) return a } return probs.length-1 }

  function runEpisode(learn=true){
    const transitions = [];
    let pos = [...start]; let total=0;
    for(let t=0;t<maxSteps;t++){
      const s = idx(pos[0],pos[1],W);
      const probs = policyProbs(s);
      const a = sampleAction(probs);
      const npos = stepState(pos,a);
      const reward = isGoal(npos) ? 1 : -0.01;
      transitions.push({s,a,r:reward});
      total += reward;
      pos = npos;
      setAgent([...pos]);
      if (isGoal(pos)) break;
    }
    if (learn) updatePolicy(transitions);
    setEpisode(e=>e+1);
    setReturnsHist(h => [total].concat(h).slice(0,200));
    return total;
  }

  function updatePolicy(transitions){
    // compute discounted returns
    const G = new Array(transitions.length);
    let runningG = 0;
    for(let t=transitions.length-1;t>=0;t--){ runningG = transitions[t].r + gamma * runningG; G[t] = runningG }
    // gradient ascent on expected return; use learning rate lr
    const p = paramsRef.current;
    // accumulate gradients for W (shape 4 x N)
    const grad = new Float32Array(p.W.length).fill(0);
    for(let t=0;t<transitions.length;t++){
      const {s,a} = transitions[t];
      const probs = policyProbs(s);
      for(let aa=0; aa<4; aa++){
        const indicator = aa===a ? 1 : 0;
        const g = (indicator - probs[aa]) * G[t];
        grad[aa*N + s] += g;
      }
    }
    const scale = lr / transitions.length;
    for(let i=0;i<p.W.length;i++) p.W[i] += scale * grad[i];
  }

  useEffect(()=>{
    let cancel=false;
    (async ()=>{
      while(running && !cancel){ await runEpisode(true); await new Promise(r=>setTimeout(r,80)); }
    })();
    return ()=>{ cancel=true };
  }, [running, lr, gamma, maxSteps]);

  function resetPolicy(){ initParams(); setEpisode(0); setReturnsHist([]); setAgent(start); }

  function renderCell(x,y){ const index = idx(x,y,W); const isWall = walls.current.has(index); const isStart = x===start[0] && y===start[1]; const isG = x===goal[0] && y===goal[1]; const probs = policyProbs(index); let bestA=0; for(let a=1;a<4;a++) if (probs[a]>probs[bestA]) bestA=a; const policyChar = ['↑','↓','←','→'][bestA];
    return (
      <div key={index} style={{ border: '1px solid var(--b0)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: isWall ? 'var(--b0)' : isG ? 'var(--gcyan)' : 'transparent' }}>
        {isStart && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>S</div>}
        {isG && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>G</div>}
        {agent[0]===x && agent[1]===y ? <div style={{ width: 28, height: 28, borderRadius: 4, background: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--void)', fontWeight: 800 }}>{'⦿'}</div> : <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{policyChar}</div>
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>{probs[bestA].toFixed(2)}</div>
        </div>}
      </div>
    );
  }

  function Sparkline({data, color='#4ade80', width=220, height=48}){
    if (!data || data.length===0) return <div style={{ width, height, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t3)' }}>—</div>;
    const vals = data.slice().reverse();
    const min = Math.min(...vals); const max = Math.max(...vals); const range = Math.max(1e-6, max-min); const step = width / Math.max(1, vals.length-1);
    const points = vals.map((v,i)=> `${i*step},${height - ((v-min)/range) * height}` ).join(' ');
    return (<svg width={width} height={height}><polyline fill="none" stroke={color} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" /></svg>);
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 18 }}>
        <div style={{ minWidth: 380 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800 }}>Policy Gradient Lab (REINFORCE)</div>
          <div style={{ marginTop: 6, color: 'var(--t2)' }}>On-policy policy gradient using a softmax linear policy with episode rollouts.</div>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(5,64px)', gap: 6 }}>
            {Array.from({length:H}).map((_,y)=> Array.from({length:W}).map((__,x)=> renderCell(x,y))).flat()}
          </div>
        </div>

        <div style={{ flex: 1, maxWidth: 520 }}>
          <div style={{ marginBottom: 12 }}>
            <HelpPanel title="Policy Gradient — Quick Tutorial" items={[
              ['Algorithm', 'REINFORCE: collect full episode, compute discounted returns, update policy via grad log π × G.'],
              ['Policy', 'Softmax linear mapping from one-hot state to action probabilities.'],
              ['Learning', 'Gradient ascent on expected return; try lowering LR if updates are unstable.'],
              ['Use', 'Train toggles continuous episodes; Step Episode runs one episode and updates.'],
            ]} />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <RunBtn running={running} onRun={() => setRunning(true)} onStop={() => setRunning(false)} label="TRAIN" />
            <button onClick={() => runEpisode(true)} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>STEP EPISODE</button>
            <button onClick={() => { setAgent(start); }} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET AGENT</button>
            <button onClick={resetPolicy} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET POLICY</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ fontSize: 12 }}>LR
              <input type="range" min="0.01" max="1" step="0.01" value={lr} onChange={e=>setLr(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Gamma
              <input type="range" min="0" max="1" step="0.01" value={gamma} onChange={e=>setGamma(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>MaxSteps
              <input type="range" min="10" max="1000" step="10" value={maxSteps} onChange={e=>setMaxSteps(parseInt(e.target.value))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Episode: <strong style={{ color: 'var(--t1)' }}>{episode}</strong></div>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Recent Returns</div>
              <div style={{ border: '1px solid var(--b0)', padding: 8, borderRadius: 4 }}><Sparkline data={returnsHist} color="#4ade80" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
